/**
 * CTR Blog Posts
 * Alpine.js component for the horizontally scrollable category tabs
 */

document.addEventListener('alpine:init', () => {
  Alpine.data('ctrBlogPosts', (config = {}) => ({
    sectionId: config.sectionId,
    blogUrl: config.blogUrl,
    activeTag: '',
    isLoading: false,
    hasOverflow: false,
    isDragging: false,
    thumbWidth: 100,
    thumbOffset: 0,
    resizeTimeout: null,
    boundResize: null,
    boundSectionLoad: null,
    boundBlockSelect: null,
    boundPopState: null,

    init() {
      const params = new URLSearchParams(window.location.search);
      this.activeTag = params.get('tag') || '';

      this.$nextTick(() => {
        this.updateActiveTab();
        this.updateScrollbar();
        this.scrollActiveTabIntoView();

        if (this.activeTag) {
          this.loadResults(this.activeTag, params.get('page') || 1, false);
        }
      });

      this.boundResize = () => {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => this.updateScrollbar(), 150);
      };
      window.addEventListener('resize', this.boundResize);

      this.boundPopState = () => {
        const currentParams = new URLSearchParams(window.location.search);
        this.loadResults(
          currentParams.get('tag') || '',
          currentParams.get('page') || 1,
          false
        );
      };
      window.addEventListener('popstate', this.boundPopState);

      this.setupShopifyEvents();
    },

    destroy() {
      clearTimeout(this.resizeTimeout);
      window.removeEventListener('resize', this.boundResize);
      window.removeEventListener('popstate', this.boundPopState);

      if (this.boundSectionLoad) {
        document.removeEventListener('shopify:section:load', this.boundSectionLoad);
      }

      if (this.boundBlockSelect) {
        document.removeEventListener('shopify:block:select', this.boundBlockSelect);
      }
    },

    selectTag(tag) {
      this.loadResults(tag, 1, true);
    },

    handleResultsClick(event) {
      const paginationLink = event.target.closest('.ctr-blog-posts__arrow[href]');
      if (!paginationLink) return;

      event.preventDefault();
      const targetUrl = new URL(paginationLink.href, window.location.origin);
      this.loadResults(this.activeTag, targetUrl.searchParams.get('page') || 1, true);
    },

    async loadResults(tag, page = 1, updateHistory = true) {
      if (!this.blogUrl || this.isLoading) return;

      this.activeTag = tag;
      this.updateActiveTab();
      this.isLoading = true;

      const storefrontUrl = tag
        ? `${this.blogUrl}/tagged/${encodeURIComponent(tag)}`
        : this.blogUrl;
      const requestUrl = new URL(storefrontUrl, window.location.origin);
      requestUrl.searchParams.set('section_id', this.sectionId);

      if (Number(page) > 1) {
        requestUrl.searchParams.set('page', page);
      }

      try {
        const response = await fetch(requestUrl.toString(), {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });

        if (!response.ok) throw new Error(`Blog request failed: ${response.status}`);

        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const nextResults = documentFragment.querySelector('.ctr-blog-posts__results');

        if (!nextResults || !this.$refs.results) {
          throw new Error('Blog results were not found in the response');
        }

        this.$refs.results.innerHTML = nextResults.innerHTML;

        if (updateHistory) {
          const visibleUrl = new URL(this.blogUrl, window.location.origin);
          if (tag) visibleUrl.searchParams.set('tag', tag);
          if (Number(page) > 1) visibleUrl.searchParams.set('page', page);
          window.history.pushState({}, '', visibleUrl);
        }

        this.$nextTick(() => {
          this.updateScrollbar();
          this.scrollActiveTabIntoView();
        });
      } catch (error) {
        console.error('[CTR Blog Posts]', error);
      } finally {
        this.isLoading = false;
      }
    },

    updateActiveTab() {
      const tabs = this.$refs.tabs;
      if (!tabs) return;

      tabs.querySelectorAll('.ctr-blog-posts__tab').forEach((tab) => {
        const isActive = (tab.dataset.tag || '') === this.activeTag;
        tab.classList.toggle('ctr-blog-posts__tab--active', isActive);

        if (isActive) {
          tab.setAttribute('aria-current', 'page');
        } else {
          tab.removeAttribute('aria-current');
        }
      });
    },

    updateScrollbar() {
      const tabs = this.$refs.tabs;
      if (!tabs) return;

      const scrollable = tabs.scrollWidth - tabs.clientWidth;
      this.hasOverflow = scrollable > 1;

      if (!this.hasOverflow) {
        this.thumbWidth = 100;
        this.thumbOffset = 0;
        return;
      }

      this.thumbWidth = (tabs.clientWidth / tabs.scrollWidth) * 100;
      this.thumbOffset = (tabs.scrollLeft / tabs.scrollWidth) * 100;
    },

    startDrag(event) {
      const track = this.$refs.scrollbar;
      if (!track || !this.hasOverflow) return;

      this.isDragging = true;

      if (track.setPointerCapture && event.pointerId !== undefined) {
        track.setPointerCapture(event.pointerId);
      }

      this.scrollToPointer(event);
    },

    moveDrag(event) {
      if (!this.isDragging) return;

      event.preventDefault();
      this.scrollToPointer(event);
    },

    endDrag(event) {
      if (!this.isDragging) return;

      this.isDragging = false;

      const track = this.$refs.scrollbar;
      if (
        track &&
        event.pointerId !== undefined &&
        track.hasPointerCapture &&
        track.hasPointerCapture(event.pointerId)
      ) {
        track.releasePointerCapture(event.pointerId);
      }
    },

    scrollToPointer(event) {
      const tabs = this.$refs.tabs;
      const track = this.$refs.scrollbar;
      if (!tabs || !track) return;

      const trackRect = track.getBoundingClientRect();
      const thumbWidth = Math.max((this.thumbWidth / 100) * trackRect.width, 24);
      const draggableWidth = trackRect.width - thumbWidth;
      if (draggableWidth <= 0) return;

      // Keeps the thumb centered under the pointer
      const pointerOffset = event.clientX - trackRect.left - thumbWidth / 2;
      const ratio = Math.min(Math.max(pointerOffset / draggableWidth, 0), 1);

      tabs.scrollLeft = ratio * (tabs.scrollWidth - tabs.clientWidth);
      this.updateScrollbar();
    },

    scrollActiveTabIntoView() {
      const tabs = this.$refs.tabs;
      if (!tabs) return;

      const activeTab = tabs.querySelector('.ctr-blog-posts__tab--active');
      if (!activeTab) return;

      tabs.scrollLeft = activeTab.offsetLeft - tabs.offsetLeft;
      this.updateScrollbar();
    },

    setupShopifyEvents() {
      if (typeof Shopify === 'undefined' || !Shopify.designMode) return;

      this.boundSectionLoad = (event) => {
        if (event.detail.sectionId !== this.sectionId) return;

        this.$nextTick(() => {
          this.updateScrollbar();
          this.scrollActiveTabIntoView();
        });
      };

      this.boundBlockSelect = (event) => {
        if (event.detail.sectionId !== this.sectionId) return;

        const tabs = this.$refs.tabs;
        if (!tabs) return;

        const tabItem = event.target.closest('.ctr-blog-posts__tab-item');
        if (!tabItem) return;

        tabs.scrollLeft = tabItem.offsetLeft - tabs.offsetLeft;
        this.updateScrollbar();
      };

      document.addEventListener('shopify:section:load', this.boundSectionLoad);
      document.addEventListener('shopify:block:select', this.boundBlockSelect);
    }
  }));
});
